/** 學生端頁面共用初始化:側欄 + 行事曆工具列 + 儀表板統計卡。
 *  自 public/js/app.js 移植(改名為 dashboard,避免與「應用程式進入點」混淆)。
 *
 *  type="module" 的腳本本身就是 defer,執行時 DOM 已解析完成,
 *  因此不再需要原本的 DOMContentLoaded 包裝。
 */
import { request } from './api';
import * as calendar from './calendar';
import { renderAll as renderPerfCharts, renderAttendance } from './charts';
import { init as initSidebar } from './sidebar';
import type {
  AttendanceRow, CalendarFilters, CreditStat, PerformanceStats, RecentExam,
} from './types';

const PIE_COLORS: Record<string, string> = {
  資電類: '#7ECEB5', 財金類: '#F2C383', 基礎必修: '#6285EA',
  管理學科: '#8CD0F2', 其他: '#C9C9C9',
};
const PIE_ORDER = ['資電類', '財金類', '基礎必修', '管理學科', '其他'];

/** 篩選標籤文字 → 事件型別 */
function labelToType(label: string): string {
  if (label === '全部') return 'all';
  if (label === '課程') return 'course';
  if (label === '作業') return 'assignment';
  return 'exam';
}

function syncFilterChecks(filters: CalendarFilters): void {
  document.querySelectorAll<HTMLElement>('.cal-bar__filter-item').forEach((item) => {
    const label = (item.textContent ?? '').trim();
    const box = item.querySelector<HTMLElement>('.cal-bar__checkbox');
    if (!box) return;
    const checked = label === '全部'
      ? filters.course && filters.assignment && filters.exam
      : filters[labelToType(label) as keyof CalendarFilters];
    box.classList.toggle('is-checked', !!checked);
    box.textContent = checked ? '✓' : '';
  });
}

/** 已修學分類型圓餅(conic-gradient + 動態圖例) */
async function renderCreditsPie(): Promise<void> {
  const pie = document.querySelector<HTMLElement>('.pie__img');
  const legend = document.querySelector<HTMLElement>('.legend-grid');
  if (!pie || !legend) return;

  const data = await request<CreditStat[]>('/api/student/stats/credits');
  const total = data.reduce((s, x) => s + x.credits, 0) || 1;
  const map: Record<string, number> = {};
  for (const x of data) map[x.category] = x.credits;

  const stops: string[] = [];
  let acc = 0;
  let legendHtml = '';
  for (const cat of PIE_ORDER) {
    const v = map[cat] ?? 0;
    if (v > 0) {
      const from = (acc / total) * 100;
      const to = ((acc + v) / total) * 100;
      stops.push(`${PIE_COLORS[cat]} ${from.toFixed(1)}% ${to.toFixed(1)}%`);
      acc += v;
    }
    legendHtml += '<div class="legend-grid__item">'
      + `<span class="legend-grid__dot" style="background:${PIE_COLORS[cat]}"></span>${cat}`
      + `<span class="legend-grid__value">${v}</span></div>`;
  }
  pie.style.background = `conic-gradient(${stops.join(',')})`;
  legend.innerHTML = legendHtml;
}

/** 出缺席長條(>3 門課可左右捲動) */
async function renderAttendanceCard(): Promise<void> {
  if (!document.getElementById('attendance-chart')) return;
  renderAttendance(await request<AttendanceRow[]>('/api/student/stats/attendance'));
}

/** 近期測驗(30 日內最多 4 筆) */
async function renderRecentExams(): Promise<void> {
  const host = document.querySelector<HTMLElement>('.test-list');
  if (!host) return;
  const data = await request<RecentExam[]>('/api/student/exams/recent');
  if (!data.length) {
    host.innerHTML = '<div style="color:#8C8C8C;font-size:14px;padding:12px 0">近 30 日內沒有測驗</div>';
    return;
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  host.innerHTML = data.map((x) => {
    const t = new Date(x.exam_at);
    const time = `${t.getMonth() + 1}/${t.getDate()} ${pad(t.getHours())}:${pad(t.getMinutes())}`;
    const meta = [x.department, `課號 ${x.code}`, x.course_type].filter(Boolean).join(' · ');
    return '<div class="test-list__item"><div>'
      + `<h2>${x.course_name} ${x.title.replace(x.course_name, '').trim()}</h2>`
      + `<h3>${meta}</h3>`
      + `</div><span class="time">${time}</span></div>`;
  }).join('');
}

function initCalendarToolbar(): void {
  const calSeg = document.getElementById('cal-seg');
  if (!calSeg) return;

  calendar.init();
  calSeg.addEventListener('click', (e) => {
    const btn = (e.target as Element | null)?.closest<HTMLElement>('[data-view]');
    if (btn?.dataset.view) calendar.setView(btn.dataset.view);
  });

  document.querySelector<HTMLElement>('.cal-bar__today-btn')
    ?.addEventListener('click', () => calendar.goToday());

  const navBtns = document.querySelectorAll<HTMLElement>('.cal-bar__nav-btn');
  navBtns[0]?.addEventListener('click', () => calendar.shift(-1));
  navBtns[1]?.addEventListener('click', () => calendar.shift(1));

  document.querySelectorAll<HTMLElement>('.cal-bar__filter-item').forEach((item) => {
    const type = labelToType((item.textContent ?? '').trim());
    item.style.cursor = 'pointer';
    item.addEventListener('click', () => syncFilterChecks(calendar.toggleFilter(type)));
  });
  syncFilterChecks(calendar.getFilters());
}

/** 學生端頁面共用啟動流程;各頁進入點只需 import 本模組 */
export async function initStudentPage(): Promise<void> {
  if (!document.querySelector('.ekm-sidebar')) return; // 登入頁不需要側欄
  try {
    await initSidebar();
  } catch {
    return; // 未登入已由 sidebar 導向登入頁
  }

  if (document.getElementById('cal-seg')) {
    initCalendarToolbar();
    await Promise.all([renderCreditsPie(), renderAttendanceCard(), renderRecentExams()]);
  }

  // 我的績效:四張圖全部由真實成績、測驗、出缺席與學習活動時間計算。
  // 失敗時在圖表區顯示錯誤,而不是留下四個空白方塊讓人以為自己沒有資料。
  if (document.getElementById('perf-radar')) {
    try {
      renderPerfCharts(await request<PerformanceStats>('/api/student/stats/performance'));
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      for (const id of ['perf-radar', 'perf-bars', 'perf-heat', 'perf-line']) {
        const host = document.getElementById(id);
        if (host) {
          host.textContent = `績效資料載入失敗:${message}`;
          host.setAttribute('style', 'padding:24px;color:#D32F2F;font-size:14px');
        }
      }
    }
  }
}
